import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';

/**
 * Security service for preventing XSS attacks
 */
@Injectable({
  providedIn: 'root'
})
export class SecurityService {
  constructor(private sanitizer: DomSanitizer) {}

  /**
   * Sanitize plain text - removes any HTML tags
   */
  sanitizeText(text: string): string {
    if (!text) return '';
    // Remove any HTML tags
    return text.replace(/<[^>]*>/g, '');
  }

  /**
   * Sanitize URLs to prevent javascript: protocol
   */
  sanitizeUrl(url: string): SafeUrl {
    if (!url) return 'javascript:void(0)' as any;
    
    // Block dangerous protocols
    if (url.toLowerCase().startsWith('javascript:') || 
        url.toLowerCase().startsWith('data:')) {
      return 'about:blank' as any;
    }
    
    return this.sanitizer.sanitize(1, url) as SafeUrl;
  }

  /**
   * Validate email format
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  /**
   * Validate password strength
   */
  isStrongPassword(password: string): boolean {
    // At least 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  }

  /**
   * Escape HTML special characters
   */
  escapeHtml(text: string): string {
    const map: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, char => map[char]);
  }

  /**
   * Validate input length to prevent excessively long inputs
   */
  isValidLength(text: string, maxLength: number = 5000): boolean {
    if (!text) return true; // Empty text is valid
    return text.trim().length <= maxLength;
  }

  /**
   * Sanitize and validate form input - combines sanitization with length validation
   */
  sanitizeFormInput(text: string, maxLength: number = 5000): string {
    if (!text) return '';
    
    // Remove HTML tags
    let sanitized = text.replace(/<[^>]*>/g, '');
    
    // Trim whitespace
    sanitized = sanitized.trim();
    
    // Limit length
    sanitized = sanitized.substring(0, maxLength);
    
    return sanitized;
  }

  /**
   * Validate phone format (allows digits, spaces, dashes, plus, parentheses)
   */
  isValidPhone(phone: string): boolean {
    if (!phone) return false;
    const phoneRegex = /^[\d\s\-\+\(\)]{5,}$/;
    return phoneRegex.test(phone);
  }

  /**
   * Rate limiting check - prevents spam by tracking submission timestamps
   * Returns true if submission should be allowed
   */
  checkRateLimit(key: string, limitPerMinute: number = 5): boolean {
    const storageKey = `rateLimit_${key}`;
    const now = Date.now();
    const submissions = sessionStorage.getItem(storageKey);
    
    let submissionTimes: number[] = submissions ? JSON.parse(submissions) : [];
    
    // Remove submissions older than 1 minute
    submissionTimes = submissionTimes.filter(time => now - time < 60000);
    
    // Check if within limit
    if (submissionTimes.length >= limitPerMinute) {
      return false;
    }
    
    // Add current submission
    submissionTimes.push(now);
    sessionStorage.setItem(storageKey, JSON.stringify(submissionTimes));
    
    return true;
  }
}
